import { computed, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { SUBSCRIPTION_WALL_ROUTE } from "@/router/routes";
import { isAuthOnlyRoute } from "@/router/auth-only-routes";

export function useSubscriptionAccessGuard() {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();

  const isAuthenticated = computed(
    () => store.getters.sessionToken != null && store.getters.account?.data != null
  );
  const orgActiveSub = computed(() => store.getters.organizationActiveSubscription?.data);
  const isFreeTrialActive = computed(() => store.getters.isFreeTrialActive);
  const hasFreeTrialDaysLeft = computed(() => store.getters.hasFreeTrialDaysLeft);
  const isViewOnlyMode = computed(() => store.getters.isViewOnlyMode);
  const subStatus = computed(() => store.getters.subscriptionStatus?.data);

  const hasPremiumAccess = computed<boolean | undefined>(() => {
    if (!isAuthenticated.value) return true;
    if (isViewOnlyMode.value) return false;
    if (isFreeTrialActive.value) return true;
    if (hasFreeTrialDaysLeft.value) return true;
    if (orgActiveSub.value) return true;
    const status = subStatus.value?.status;
    if (subStatus.value?.hasSubscription && (status === "active" || status === "trialing")) {
      return true;
    }
    // The subscription-status endpoint returns status="trialing" for free trial users
    // even when hasSubscription=false. Trust this signal to avoid paywall flash on login.
    if (status === "trialing" && subStatus.value != null) return true;
    if (subStatus.value == null && orgActiveSub.value == null) {
      return undefined;
    }
    return false;
  });

  watch(hasPremiumAccess, (now, prev) => {
    if (now !== false) return;
    if (prev === undefined) return;
    if (route.name === SUBSCRIPTION_WALL_ROUTE) return;
    if (isAuthOnlyRoute(route.name as string | undefined, route.path)) return;
    router.replace({ name: SUBSCRIPTION_WALL_ROUTE });
  });
}
